<template>
  <section
    class="my-lists-sidebar-element"
    :class="{ 'my-lists-sidebar-element--collapsed': collapsed }"
  >
    <div
      class="create-wrapper"
    >
      <div
        class="create-button"
        :title="collapsedTitle('create_list')"
        @click="createList()"
      >
        <div class="bloc-icon">
          <svg-element
            icon="plus"
            size="12"
          />
        </div>
        <span class="text nds-text-link">
          {{ $t('create_list') }}
        </span>
      </div>
    </div>

    <section class="sidebar-section">
      <span class="sidebar-title">
        {{ $t('my_lists') }}
      </span>

      <template
        v-for="link in link_list"
        :key="link.key"
      >
        <router-link
          v-if="link.to"
          class="sidebar-link"
          :title="collapsedTitle(link.label)"
          :to="link.to"
        >
          <span class="sidebar-link-icon">
            <svg-element
              :icon="link.icon"
              size="16"
            />
          </span>

          <span class="sidebar-link-text nds-text-p-medium">
            {{ $t(link.label) }}
            <template v-if="link.count !== null">
              ({{ link.count }})
            </template>
          </span>
        </router-link>

        <a
          v-else
          class="sidebar-link"
          :title="collapsedTitle(link.label)"
          :href="link.href"
        >
          <span class="sidebar-link-icon">
            <svg-element
              :icon="link.icon"
              size="16"
            />
          </span>

          <span class="sidebar-link-text nds-text-p-medium">
            {{ $t(link.label) }}
            <template v-if="link.count !== null">
              ({{ link.count }})
            </template>
          </span>
        </a>
      </template>
    </section>

    <create-lists-info-popup
      v-model="create_lists_info_popup_open"
      @confirm="goToAdvancedSearch()"
    />
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import configuration from '@/configurations';
import { QUERY_CONSTANTS } from '@/constants/QueryConstants';

import CreateListsInfoPopup, {
  getMyListsInfoPopupHidden,
} from '@/components/elements/my-lists/CreateListsInfoPopup.vue';
import SvgElement from '@/components/elements/SvgElement.vue';

const COMPANY_LIST_TYPE_ID = QUERY_CONSTANTS.TYPES.PROSPECTING_ACCOUNT;
const CONTACT_LIST_TYPE_ID = QUERY_CONSTANTS.TYPES.PROSPECTING_CONTACT;

export default {
  name: 'MyListsSidebarElement',
  components: {
    CreateListsInfoPopup,
    SvgElement,
  },
  props: {
    collapsed: {
      default: false,
      type: Boolean,
    },
  },
  data: () => ({
    create_lists_info_popup_open: false,
    link_list: [
      {
        key: 'contact-lists',
        icon: 'user-sign',
        label: 'my_contact_lists',
        count: null,
        to: { name: 'MyListsContactsPage' },
      },
      {
        key: 'company-lists',
        icon: 'paper',
        label: 'my_company_lists',
        count: null,
        to: { name: 'MyListsCompaniesPage' },
      },
      {
        key: 'automatic-lists',
        icon: 'line-clock',
        label: 'automatic_lists',
        count: null,
        to: { name: 'MyListsAutomaticPage' },
      },
      {
        key: 'unsubscribed-lists',
        icon: 'user-cancel',
        label: 'unsubscribed_lists',
        count: null,
        to: { name: 'MyListsIgnorePage' },
      },
      {
        key: 'my-prospects',
        icon: 'prospects',
        label: 'my_prospects',
        count: null,
        href: `${configuration.desk_v1_host}/my-prospecting/my-prospects`,
      },
    ],
  }),
  computed: {
    ...mapGetters({
      query_count_list: 'query/queryCountList',
    }),
  },
  watch: {
    query_count_list: {
      handler() {
        this.applyQueryCounts();
      },
    },
  },
  mounted() {
    this.loadListCounts();
  },
  methods: {
    ...mapActions({
      getUserQueryCounters: 'query/getUserQueryCounters',
    }),
    collapsedTitle(label_key) {
      return this.collapsed ? this.$t(label_key) : undefined;
    },
    applyQueryCounts() {
      const count_by_type_id = new Map(
        (this.query_count_list || []).map((item) => [item.id, item.count])
      );

      this.setListCount('company-lists', count_by_type_id.get(COMPANY_LIST_TYPE_ID) ?? 0);
      this.setListCount('contact-lists', count_by_type_id.get(CONTACT_LIST_TYPE_ID) ?? 0);
    },
    async loadListCounts() {
      try {
        await this.getUserQueryCounters();
        this.applyQueryCounts();
      } catch {
        this.setListCount('company-lists', 0);
        this.setListCount('contact-lists', 0);
      }
    },
    setListCount(link_key, count) {
      const link = this.link_list.find((item) => item.key === link_key);

      if (!link) {
        return;
      }

      link.count = count;
    },
    createList() {
      if (getMyListsInfoPopupHidden()) {
        this.goToAdvancedSearch();

        return;
      }

      this.create_lists_info_popup_open = true;
    },
    goToAdvancedSearch() {
      window.open(`${configuration.desk_v1_host}/advanced-search/positions`, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/constants/variables.module.scss';

$sidebar-content-transition: 0.35s cubic-bezier(0.4, 0, 0.2, 1);

@mixin collapsed-sidebar-icon-button {
  align-items: center;
  background-color: $white;
  border-radius: 8px;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  margin-inline: auto;
  width: 32px;

  svg {
    --color: #{$amethyst-smoke};
  }

  &:hover {
    background-color: $zumthor;

    svg {
      --color: #{$medium-slate-blue} !important;
    }
  }
}

.my-lists-sidebar-element {
  display: flex;
  flex-direction: column;
  font-family: 'DM Sans', sans-serif;
  gap: 24px;
  width: 100%;

  &--collapsed {
    align-items: center;

    .create-wrapper {
      align-self: center;
      flex-shrink: 0;
      width: 32px;
    }

    .sidebar-section {
      align-items: center;
      width: 100%;
    }

    .sidebar-title,
    .sidebar-link-text {
      display: none;
    }

    .sidebar-link {
      @include collapsed-sidebar-icon-button;
      gap: 0;

      &.router-link-active {
        background-color: $zumthor;

        .sidebar-link-icon svg {
          --color: #{$cornflower-blue} !important;
        }

        &:hover .sidebar-link-icon svg {
          --color: #{$medium-slate-blue} !important;
        }
      }
    }
  }
}

.create-wrapper {
  align-self: flex-start;
  position: relative;
  width: 100%;

  .create-button {
    align-items: center;
    background-color: $cornflower-blue;
    border-radius: 12px;
    display: flex;
    gap: 8px;
    justify-content: center;
    padding: 8px 12px;
    user-select: none;
    width: 100%;

    &:hover {
      background-color: $medium-slate-blue;
      cursor: pointer;
    }

    svg {
      --color: #{$white};
    }

    .text {
      color: $white;
    }
  }

  .bloc-icon {
    align-items: center;
    display: flex;
    height: 16px;
    justify-content: center;
    width: 16px;
  }
}

.my-lists-sidebar-element--collapsed .create-wrapper {
  .create-button {
    border-radius: 8px;
    height: 32px;
    justify-content: center;
    padding: 0;
    width: 32px;

    svg {
      --color: #{$white};
    }

    .text {
      display: none;
    }
  }
}

.sidebar-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sidebar-title {
  color: $amethyst-smoke;
  line-height: 16px;
  min-height: 16px;
  overflow: hidden;
  text-transform: uppercase;
  transition: opacity 0.2s ease;
  white-space: nowrap;
}

.sidebar-link {
  align-items: center;
  border: none;
  display: flex;
  gap: 12px;
  height: 22px;
  outline: none;
  text-decoration: none;
  transition: background-color 0.2s ease, gap $sidebar-content-transition;
  width: 100%;

  svg {
    --color: #{$amethyst-smoke};
  }

  &:hover {
    .sidebar-link-text {
      color: $medium-slate-blue !important;
    }

    svg {
      --color: #{$medium-slate-blue} !important;
    }
  }
}

.sidebar-link-icon {
  display: flex;
  flex-shrink: 0;
  text-align: center;
}

.sidebar-link-text {
  color: $haiti;
  max-width: 200px;
  opacity: 1;
  overflow: hidden;
  transition: max-width $sidebar-content-transition, opacity 0.2s ease;
  white-space: nowrap;
}

.router-link-active {
  .sidebar-link-text {
    color: $cornflower-blue !important;
  }

  .sidebar-link-icon svg {
    --color: #{$cornflower-blue} !important;
  }

  &:hover .sidebar-link-icon svg {
    --color: #{$medium-slate-blue} !important;
  }
}
</style>

<i18n src="@/locales/components/elements/my-lists/my-lists-sidebar-element.json"></i18n>
